import React, { useEffect, useRef, useState } from "react";

// Lifetime ni starting date: tamari stats chalu thaya ni date rakho
const LIFETIME_START = "2026-01-01";

const pad = (n) => String(n).padStart(2, "0");

// local (IST) date => YYYY-MM-DD
export const fmt = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const parse = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const addDays = (d, n) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

export function getPresetRange(key) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (key) {
    case "today":
      return { from: fmt(today), to: fmt(today) };
    case "yesterday": {
      const y = addDays(today, -1);
      return { from: fmt(y), to: fmt(y) };
    }
    case "thisWeek": {
      const start = addDays(today, -today.getDay()); // Sunday
      return { from: fmt(start), to: fmt(addDays(start, 6)) };
    }
    case "lastWeek": {
      const start = addDays(today, -today.getDay() - 7);
      return { from: fmt(start), to: fmt(addDays(start, 6)) };
    }
    case "thisMonth":
      return {
        from: fmt(new Date(today.getFullYear(), today.getMonth(), 1)),
        to: fmt(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
    case "lastMonth":
      return {
        from: fmt(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        to: fmt(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
    case "thisYear":
      return {
        from: fmt(new Date(today.getFullYear(), 0, 1)),
        to: fmt(new Date(today.getFullYear(), 11, 31)),
      };
    case "lastYear":
      return {
        from: fmt(new Date(today.getFullYear() - 1, 0, 1)),
        to: fmt(new Date(today.getFullYear() - 1, 11, 31)),
      };
    case "lifetime":
      return { from: LIFETIME_START, to: fmt(today) };
    default:
      return { from: fmt(today), to: fmt(today) };
  }
};

const niceDate = (s) =>
  parse(s).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/* ---------------- inline range calendar ---------------- */

const RangeCalendar = ({ from, to, onPick }) => {
  const todayStr = fmt(new Date());

  const [view, setView] = useState(() => {
    const base = from ? parse(from) : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const monthLabel = view.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const gridStart = addDays(view, -view.getDay());
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));

  // aavta mahina ni 1 tarikh future hoy to next band
  const nextDisabled =
    fmt(new Date(view.getFullYear(), view.getMonth() + 1, 1)) > todayStr;

  const goPrev = () =>
    setView(new Date(view.getFullYear(), view.getMonth() - 1, 1));
  const goNext = () => {
    if (!nextDisabled)
      setView(new Date(view.getFullYear(), view.getMonth() + 1, 1));
  };

  return (
    <div className="w-full max-w-[280px] overflow-hidden rounded-md border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800">
      {/* header */}
      <div className="flex items-center justify-between bg-gray-100 px-3 py-2 dark:bg-gray-700">
        <button
          type="button"
          onClick={goPrev}
          className="px-2 text-lg leading-none text-gray-600 hover:text-gray-900 dark:text-gray-300"
        >
          ‹
        </button>
        <span className="text-base font-bold text-gray-900 dark:text-white">
          {monthLabel}
        </span>
        <button
          type="button"
          onClick={goNext}
          disabled={nextDisabled}
          className="px-2 text-lg leading-none text-gray-600 hover:text-gray-900 disabled:cursor-not-allowed disabled:text-gray-300 dark:text-gray-300"
        >
          ›
        </button>
      </div>

      {/* weekdays */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-100 pb-1 text-center text-sm font-bold text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
        {WEEKDAYS.map((w) => (
          <div key={w}>{w}</div>
        ))}
      </div>

      {/* days */}
      <div className="grid grid-cols-7 gap-y-1 p-2 text-center text-sm">
        {cells.map((d) => {
          const ds = fmt(d);
          const inMonth = d.getMonth() === view.getMonth();
          const isFuture = ds > todayStr;
          const isStart = ds === from;
          const isEnd = ds === to;
          const inRange = from && to && ds > from && ds < to;

          let cls = "mx-auto flex h-8 w-8 items-center justify-center rounded ";

          if (isFuture) {
            cls += "cursor-not-allowed text-gray-300 dark:text-gray-600";
          } else if (isStart || isEnd) {
            cls += "bg-brand-500 font-semibold text-white";
          } else if (inRange) {
            cls += "bg-brand-100 text-gray-900";
          } else if (!inMonth) {
            cls += "text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700";
          } else {
            cls +=
              "text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700";
          }

          return (
            <button
              key={ds}
              type="button"
              disabled={isFuture}
              onClick={() => onPick(ds)}
              className={cls}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ---------------- date filter ---------------- */

const DateFilter = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("today");
  const [customFrom, setCustomFrom] = useState(value.from);
  const [customTo, setCustomTo] = useState(value.to);
  const boxRef = useRef(null);

  const now = new Date();
  const monthName = (d) => d.toLocaleDateString("en-US", { month: "long" });
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const options = [
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "thisWeek", label: "This Week (Sunday to Saturday)" },
    { key: "lastWeek", label: "Last Week (Sunday to Saturday)" },
    {
      key: "thisMonth",
      label: `This Month (${monthName(now)} ${now.getFullYear()})`,
    },
    {
      key: "lastMonth",
      label: `Last Month (${monthName(lastMonthDate)} ${lastMonthDate.getFullYear()})`,
    },
    { key: "thisYear", label: `This Year (${now.getFullYear()})` },
    { key: "lastYear", label: `Last Year (${now.getFullYear() - 1})` },
    { key: "lifetime", label: "Lifetime" },
    { key: "custom", label: "Custom Range" },
  ];

  // bahar click kare to band
  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // calendar ma date click: pehla click = from, biju click = to
  const handlePick = (d) => {
    setSelected("custom");

    if (!customFrom || (customFrom && customTo)) {
      setCustomFrom(d);
      setCustomTo("");
    } else if (d < customFrom) {
      setCustomTo(customFrom);
      setCustomFrom(d);
    } else {
      setCustomTo(d);
    }
  };

  const handleApply = () => {
    if (selected === "custom") {
      if (!customFrom) return;
      // fakt ek date click kari hoy to e j divas
      onChange({ from: customFrom, to: customTo || customFrom });
    } else {
      onChange(getPresetRange(selected));
    }
    setOpen(false);
  };

  const handleClear = () => {
    setSelected("today");
    const r = getPresetRange("today");
    setCustomFrom(r.from);
    setCustomTo(r.to);
    onChange(r);
    setOpen(false);
  };

  return (
    <div className="relative inline-block" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
        Date Filter: {niceDate(value.from)} - {niceDate(value.to)}
      </button>

      {open && (
        <div className="absolute left-0 z-40 mt-2 w-[min(92vw,640px)] rounded-xl border border-gray-200 bg-white p-5 shadow-xl dark:border-gray-700 dark:bg-gray-900">
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <h4 className="mb-3 text-base font-semibold text-gray-700 dark:text-gray-200">
                Select Range
              </h4>
              <ul className="flex flex-col gap-1">
                {options.map((o) => {
                  const active = selected === o.key;
                  return (
                    <li key={o.key}>
                      <label
                        className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm ${
                          active
                            ? "border-l-4 border-brand-500 bg-brand-50 text-gray-900"
                            : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                        }`}
                      >
                        <input
                          type="radio"
                          name="date-range"
                          checked={active}
                          onChange={() => setSelected(o.key)}
                          className="accent-brand-500"
                        />
                        {o.label}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div>
              <h4 className="mb-3 text-base font-semibold text-gray-700 dark:text-gray-200">
                Custom Range
              </h4>

              <RangeCalendar from={customFrom} to={customTo} onPick={handlePick} />

              <p className="mt-3 text-xs text-gray-500">
                {customFrom
                  ? `${niceDate(customFrom)} - ${niceDate(customTo || customFrom)}`
                  : "Select a date"}
              </p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={handleClear}
              className="rounded-lg bg-gray-100 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-200"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="rounded-lg bg-brand-500 py-3 text-sm font-semibold text-white"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilter;